<template>
    <div class="currency-percentage-input-wrapper">
        <CoreTwoColumnsRow
            :showBorder="showBorder"
            :error="isError"
            :class="classes"
        >
            <template #left>
                <CorePercentageInput
                    v-if="!inverse"
                    v-model="percentage"
                    @focus="() => (isPercentEdited = false)"
                    @input="() => (isPercentEdited = true)"
                    :min="0"
                    :max="maxPercent"
                    :inline="true"
                    :maxFractionDigits="percentageDigits ?? 2"
                    :minFractionDigits="percentageDigits ?? 1"
                    :step="0.5"
                    :showButtons="true"
                />
                <CoreCurrencyInput
                    v-else
                    v-model="value"
                    :showBorder="false"
                    :currency="currency"
                    :maxFractionDigits="currencyDigits"
                    @focus="() => (isFocused = true)"
                    @blur="() => (isFocused = false)"
                    :textAlign="currencyInputTextAlign"
                />
            </template>
            <template #right>
                <CoreCurrencyInput
                    v-if="!inverse"
                    v-model="value"
                    :showBorder="false"
                    :currency="currency"
                    :maxFractionDigits="currencyDigits"
                    @focus="() => (isFocused = true)"
                    @blur="() => (isFocused = false)"
                    :textAlign="currencyInputTextAlign"
                />
                <CorePercentageInput
                    v-else
                    v-model="percentage"
                    @focus="() => (isPercentEdited = false)"
                    @input="() => (isPercentEdited = true)"
                    :min="0"
                    :max="maxPercent"
                    :inline="true"
                    :maxFractionDigits="percentageDigits ?? 2"
                    :minFractionDigits="percentageDigits ?? 1"
                    :step="0.5"
                    :showButtons="true"
                />
            </template>
        </CoreTwoColumnsRow>

        <CoreInputNotification
            v-if="isFocused"
            :customNotificationMessage="customNotificationMessage"
        />
    </div>
</template>

<script>
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreCurrencyInput from '@/components/core/CoreCurrencyInput.vue'
import CorePercentageInput from '@/components/core/CorePercentageInput.vue'
import CoreInputNotification from '@/components/core/CoreInputNotification.vue'

export default {
    name: 'CurrencyPercentageInput',

    components: {
        CoreTwoColumnsRow,
        CoreCurrencyInput,
        CorePercentageInput,
        CoreInputNotification,
    },

    props: {
        modelValue: {
            type: [Number, String, null],
            validator(value) {
                return value === null || !isNaN(Number(value))
            },
            required: true,
        },

        baseValue: {
            type: Number,
            required: true,
        },

        maxPercent: {
            type: Number,
            default: 100,
        },

        customNotificationMessage: {
            type: Object,
            default: null,
        },

        currency: {
            type: String,
            default: null,
        },

        currencyDigits: {
            type: Number,
            default: 0,
        },

        inverse: {
            type: Boolean,
            default: false,
        },

        showBorder: {
            type: Boolean,
            default: true,
        },

        currencyInputTextAlign: {
            type: String,
            default: 'right',
        },

        percentageDigits: {
            type: Number,
            default: null,
        },

        // Store a %-derived amount as shown (e.g. in cents) instead of the raw product
        roundToCurrencyDigits: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue'],

    data() {
        return {
            isFocused: false,
            isPercentEdited: false,
        }
    },

    computed: {
        value: {
            get() {
                return this.modelValue
            },
            set(value) {
                this.$emit('update:modelValue', value)
            },
        },

        percentage: {
            get() {
                if (this.baseValue === 0) return Number(0)

                const value = (100 * this.modelValue) / this.baseValue
                return value <= this.maxPercent
                    ? Number(value.toFixed(this.percentageDigits ?? 2))
                    : Number(this.maxPercent)
            },
            set(percent) {
                if (percent === this.percentage && !this.isPercentEdited) return

                this.$emit(
                    'update:modelValue',
                    this.roundToCurrencyDigits
                        ? this.roundedAmount(percent)
                        : (this.baseValue * percent) / 100,
                )
            },
        },

        classes() {
            return {
                'currency-percentage-input': true,
            }
        },

        isError() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.hasOwnProperty('severity') &&
                this.customNotificationMessage.severity === 'error'
            ) {
                return true
            }

            return false
        },
    },

    methods: {
        // Half-up in whole currency units. toFixed rounds exact half-cents down (10 % of 1000.35
        // → 100.03), and Math.round(x * 100) still does for bases that aren't exact in binary
        // (50 % of 0.29 → 0.14), so the amount is computed from integers
        roundedAmount(percent) {
            const unit = 10 ** this.currencyDigits
            const percentUnit = 10 ** (this.percentageDigits ?? 2)
            const baseUnits = Math.round(this.baseValue * unit)
            const percentUnits = Math.round(percent * percentUnit)

            return Math.round((baseUnits * percentUnits) / (100 * percentUnit)) / unit
        },
    },
}
</script>

<style>
.currency-percentage-input-wrapper {
    position: relative;

    & .currency-percentage-input.core-two-columns-row.core-two-columns-row--show-border {
        height: 5.1rem;
        padding: 0;
        border: none;

        & > span.core-percentage-input > input {
            width: 10rem;
        }
    }
}
</style>
